import { useEffect, useState } from "react";
import { Play, X } from "lucide-react";

/**
 * IntroVideoButton — Inline CTA + Lightbox Modal
 *
 * Renders a compact "Watch Intro Video" trigger (thumbnail + play icon + label)
 * that opens a full-screen lightbox overlay with an inline HTML5 video player.
 *
 * Replaces the previous floating right-edge pill (which linked out to YouTube
 * and failed to play due to embed restrictions on the owner's video).
 *
 * To swap the video, update INTRO_VIDEO_URL below.
 */
const INTRO_VIDEO_URL = "/video/intro.mp4";

const INTRO_VIDEO_POSTER = "/video/intro_poster.jpg";

export default function IntroVideoButton() {
  const [isOpen, setIsOpen] = useState(false);

  // Lock body scroll + close on Esc while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen]);

  return (
    <>
      {/* Desktop/tablet trigger — vertical pill matching the Laminate/Fabric/Glass shade card width (48px)
          Positioned below the shade stack (144px tall centered at 50% → ends at 50% + 72px) */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label="Watch Ezeefit introduction video"
        data-testid="intro-video-btn"
        className="hidden md:flex fixed right-0 z-40 bg-[#0F172A] hover:bg-[#B91C1C] active:opacity-85 text-white py-2 px-1 flex-col items-center justify-center gap-1.5 rounded-bl-lg shadow-xl transition-colors"
        style={{ top: "calc(50% + 72px)", width: "48px", height: "120px" }}
      >
        {/* Circular video preview with WhatsApp-style ping ring */}
        <span className="relative flex items-center justify-center shrink-0">
          <span className="absolute inline-flex w-8 h-8 rounded-full bg-[#F97316] opacity-75 animate-ping" aria-hidden="true" />
          <span className="relative w-8 h-8 rounded-full overflow-hidden ring-2 ring-[#F97316] bg-black">
            <img
              src={INTRO_VIDEO_POSTER}
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/30">
              <Play className="w-3 h-3 ml-0.5 text-white" fill="currentColor" strokeWidth={0} />
            </span>
          </span>
        </span>
        <span
          className="text-[9px] font-bold uppercase tracking-wider whitespace-nowrap leading-none"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          Intro
        </span>
      </button>

      {/* Mobile trigger — narrow vertical pill matching the mobile shade strips width (20px)
          Positioned below the shade stack (3 × 60px = 180px tall centered at 50% → ends at 50% + 90px) */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label="Watch Ezeefit introduction video"
        data-testid="intro-video-btn-mobile"
        className="md:hidden fixed right-0 z-50 bg-[#0F172A] active:opacity-85 text-white py-2 px-0.5 flex flex-col items-center justify-center gap-1 rounded-bl-md shadow-xl overflow-hidden"
        style={{ top: "calc(50% + 90px)", width: "24px", height: "76px" }}
      >
        <span className="relative flex items-center justify-center shrink-0">
          <span className="absolute inline-flex w-4 h-4 rounded-full bg-[#F97316] opacity-70 animate-ping" aria-hidden="true" />
          <span className="relative w-4 h-4 rounded-full overflow-hidden ring-1 ring-[#F97316] bg-black">
            <img
              src={INTRO_VIDEO_POSTER}
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/30">
              <Play className="w-2 h-2 ml-0.5 text-white" fill="currentColor" strokeWidth={0} />
            </span>
          </span>
        </span>
        <span
          className="text-[7px] font-bold uppercase tracking-wider whitespace-nowrap leading-none"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          Intro
        </span>
      </button>

      {/* Lightbox Modal */}
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Ezeefit introduction video"
          data-testid="intro-video-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 md:p-8"
          onClick={() => setIsOpen(false)}
        >
          {/* Close button — top right of viewport */}
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label="Close intro video"
            data-testid="intro-video-close"
            className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors z-10"
          >
            <X className="w-5 h-5" strokeWidth={2.25} />
          </button>

          {/* Video container — stop click bubbling so clicking the player doesn't close */}
          <div
            className="relative w-full max-w-5xl aspect-video bg-black shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={INTRO_VIDEO_URL}
              className="w-full h-full object-contain bg-black"
              autoPlay
              controls
              playsInline
              preload="auto"
              data-testid="intro-video-player"
            />
          </div>
        </div>
      )}
    </>
  );
}
